/* BB Cart Proof Pop-up (Oct 2026)
 *
 * Who sees it: a cart line has a pet photo, the shopper is not logged in
 * (the section does not render for customers), they have not submitted the
 * pop-up in this browser, and it has not shown in the last N days.
 *
 * When:
 *   Trigger A  the cart drawer closes (or they leave /cart) without Checkout,
 *              then N seconds pass while our store is on screen. Resets on
 *              Add to Cart or reopening the cart. Carries across store pages.
 *   Trigger B  they tapped Checkout and came back to the store in the same
 *              visit with the cart still full: shows 3 seconds after the page.
 *   Trigger C  (theme editor switch) about to leave: cursor heads for the tab
 *              bar on desktop, first tap of Back on phones.
 *
 * Submit: posts text/plain no-cors to the lead endpoint (the page cannot read
 * the reply; the proof of success is the row in Proof Leads), saves the email
 * as the hidden cart attribute _proof_email, fires Meta Lead.
 *
 * Test: add ?bbproof=test to any URL. Wait drops to 5s, the rest days and the
 * submitted flag are ignored for this tab. ?bbproof=off ends test mode.
 *
 * Self-contained IIFE, every storage key prefixed bbcp_. Reads the cart drawer
 * from outside (attribute observer, click listener); never edits it.
 */
(function () {
  'use strict';

  if (window.__bbcpLoaded) return;
  window.__bbcpLoaded = true;

  var PHOTO_KEYS = ['Upload Pet Photo', '_Pet Photo URL', 'Pet Photo URL', 'Pet Photo'];
  var NAME_KEYS = ["Pet's Name", 'Pet’s Name', 'Pet Name', 'Pets Name'];
  var PLACEHOLDER_NAMES = ['my pet', 'pet', 'your pet'];
  var CHECKOUT_SELECTOR = [
    'button[name="checkout"]', 'input[name="checkout"]', '#checkout',
    'a[href*="/checkout"]', '[data-bbcp-checkout]',
    '.shopify-payment-button', 'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart', '.additional-checkout-buttons'
  ].join(',');

  var K = {
    submitted: 'bbcp_submitted',
    shownAt: 'bbcp_shown_at',
    armed: 'bbcp_a_armed',
    elapsed: 'bbcp_a_elapsed',
    checkout: 'bbcp_checkout',
    test: 'bbcp_test',
    utm: 'bbcp_utm',
    landing: 'bbcp_landing'
  };

  /* ------------------------------------------------------------ storage */

  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) {} }
  function ssDel(k) { try { window.sessionStorage.removeItem(k); } catch (e) {} }

  /* ------------------------------------------------------------ helpers */

  function root() {
    try { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; } catch (e) { return '/'; }
  }

  function isTest() { return ssGet(K.test) === '1'; }

  function track(name, data) {
    var payload = data || {};
    try { if (window.dataLayer) window.dataLayer.push(Object.assign({ event: 'bbcp_' + name }, payload)); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', 'bbcp_' + name, payload); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('bbcp_' + name, payload); } catch (e) {}
  }

  function fbqLead() {
    try { if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { content_name: 'cart_proof' }); } catch (e) {}
  }

  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim()); }

  function capFirst(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  /* -------------------------------------------- first-touch UTMs (session) */

  (function captureSource() {
    var out = {};
    try {
      var q = new URLSearchParams(window.location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid']
        .forEach(function (k) { if (q.get(k)) out[k] = q.get(k); });
      var flag = q.get('bbproof');
      if (flag === 'test') ssSet(K.test, '1');
      if (flag === 'off') ssDel(K.test);
    } catch (e) {}
    if (Object.keys(out).length && !ssGet(K.utm)) ssSet(K.utm, JSON.stringify(out));
    if (!ssGet(K.landing)) ssSet(K.landing, JSON.stringify({ page: window.location.href, referrer: document.referrer || '' }));
  })();

  /* ------------------------------------------------------------ the cart */

  function fetchCart() {
    return fetch(root() + 'cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  function propsOf(item) { return (item && item.properties) || {}; }

  function photoOf(item) {
    var p = propsOf(item), i, k;
    for (i = 0; i < PHOTO_KEYS.length; i++) {
      if (/^https?:\/\//i.test(String(p[PHOTO_KEYS[i]] || ''))) return String(p[PHOTO_KEYS[i]]);
    }
    for (k in p) {
      if (/photo/i.test(k) && /^https?:\/\//i.test(String(p[k] || ''))) return String(p[k]);
    }
    return '';
  }

  function petNameOf(item) {
    var p = propsOf(item), i, k, v = '';
    for (i = 0; i < NAME_KEYS.length && !v; i++) v = String(p[NAME_KEYS[i]] || '').trim();
    if (!v) for (k in p) { if (/^pet.{0,3}\s?name$/i.test(k)) { v = String(p[k] || '').trim(); break; } }
    if (PLACEHOLDER_NAMES.indexOf(v.toLowerCase()) !== -1) v = '';
    return v;
  }

  /** The photo line to talk about: Shopify lists the newest line first. */
  function readCart(cart) {
    var items = (cart && cart.items) || [];
    var photoLines = items.filter(function (it) { return !!photoOf(it); });
    return {
      cart: cart,
      line: photoLines[0] || null,
      photoLines: photoLines.length,
      hasBundle: items.some(function (it) { return !!propsOf(it)._bundle_id; })
    };
  }

  /* ------------------------------------------------------- eligibility */

  function restDays(el) { var d = parseInt(el.getAttribute('data-rest-days'), 10); return isNaN(d) ? 7 : d; }

  function shopperAllowed(el) {
    if (isTest()) return true;
    if (lsGet(K.submitted)) return false;
    var at = parseInt(lsGet(K.shownAt) || '0', 10);
    if (at && Date.now() - at < restDays(el) * 86400000) return false;
    return true;
  }

  /* ------------------------------------------------------------ the dialog */

  var state = { el: null, info: null, trigger: '', open: false, shownThisPage: false, preview: false };

  function dlg() {
    if (!state.el || !document.body.contains(state.el)) state.el = document.querySelector('dialog[data-bbcp]');
    return state.el;
  }

  function otherDialogOpen() {
    return !!document.querySelector('dialog[open]:not([data-bbcp])');
  }

  function fill(el, pet, email) {
    var fallback = el.getAttribute('data-pet-fallback') || 'your pet';
    var name = pet || fallback;
    el.querySelectorAll('[data-bbcp-tpl]').forEach(function (node) {
      var tpl = node.getAttribute('data-bbcp-tpl') || '';
      var text = tpl.split('{pet}').join(name).split('{email}').join(email || '');
      node.textContent = pet ? text : capFirst(text);
    });
  }

  function setView(el, name) {
    el.querySelectorAll('[data-bbcp-view]').forEach(function (v) {
      v.hidden = v.getAttribute('data-bbcp-view') !== name;
    });
  }

  function openDialog(el) {
    document.documentElement.classList.add('bbcp-lock');
    try { el.showModal(); } catch (e) { el.setAttribute('open', ''); }
    state.open = true;
    // Do not pop the phone keyboard on open; focus the card instead.
    try { el.querySelector('.bbcp__card').setAttribute('tabindex', '-1'); el.querySelector('.bbcp__card').focus({ preventScroll: true }); } catch (e) {}
  }

  function closeDialog(reason) {
    var el = dlg();
    if (!el) return;
    if (el.open) { try { el.close(); } catch (e) { el.removeAttribute('open'); } }
    document.documentElement.classList.remove('bbcp-lock');
    el.classList.remove('bbcp--kb');
    if (state.open && !state.preview) track('close', { trigger: state.trigger, reason: reason || 'close' });
    state.open = false;
  }

  /** Re-checks everything against a fresh cart, then shows. */
  function show(trigger) {
    var el = dlg();
    if (!el || state.open || state.shownThisPage) return Promise.resolve(false);
    if (!shopperAllowed(el)) return Promise.resolve(false);

    return fetchCart().then(function (cart) {
      var info = readCart(cart);
      if (!info.line) return false;
      if (otherDialogOpen()) return false;
      if (state.open || state.shownThisPage) return false;

      state.info = info;
      state.trigger = trigger;
      state.shownThisPage = true;
      disarmA();

      // The three step images are fixed in the theme editor (Uyen, 15 Oct).
      // The shopper's own photo is not shown here; it only goes to the endpoint.
      fill(el, petNameOf(info.line), '');
      setView(el, 'offer');
      var err = el.querySelector('[data-bbcp-error]');
      if (err) err.hidden = true;

      lsSet(K.shownAt, String(Date.now()));
      openDialog(el);
      track('show', { trigger: trigger });
      return true;
    }).catch(function () { return false; });
  }

  /* ------------------------------------------------------------ submit */

  function buildPayload(email) {
    var info = state.info || {};
    var line = info.line || {};
    var utm = {}, landing = {};
    try { utm = JSON.parse(ssGet(K.utm) || '{}'); } catch (e) {}
    try { landing = JSON.parse(ssGet(K.landing) || '{}'); } catch (e) {}

    var data = {
      event: 'cart_proof_' + (state.trigger || 'a'),
      email: email,
      pet_name: petNameOf(line),
      photo_url: photoOf(line),
      variant_id: line.variant_id || line.id || '',
      variant_title: line.variant_title || '',
      price: typeof line.final_price === 'number' ? (line.final_price / 100).toFixed(2) : '',
      product: line.product_title || '',
      cart_lines: info.photoLines || 0,
      has_bundle: info.hasBundle ? 'yes' : 'no',
      cart_token: (info.cart && info.cart.token) || '',
      page: window.location.href,
      referrer: landing.referrer || document.referrer || '',
      landing_page: landing.page || '',
      submitted_at: new Date().toISOString()
    };
    (line.options_with_values || []).forEach(function (o, i) {
      data['option_' + (i + 1) + '_' + o.name] = o.value;
    });
    Object.keys(utm).forEach(function (k) { data[k] = utm[k]; });
    return data;
  }

  function send(endpoint, data) {
    if (!endpoint) return Promise.resolve();
    return fetch(endpoint, {
      method: 'POST',
      mode: 'no-cors',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(data)
    }).catch(function () {});
  }

  function saveEmailOnCart(email) {
    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { _proof_email: email } })
    }).catch(function () {});
  }

  function onSubmit(e) {
    e.preventDefault();
    var el = dlg();
    var input = el.querySelector('[data-bbcp-email]');
    var err = el.querySelector('[data-bbcp-error]');
    var btn = el.querySelector('.bbcp__form .bbcp__btn');
    var email = String(input.value || '').trim().toLowerCase();

    if (!validEmail(email)) {
      err.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.focus();
      return;
    }
    err.hidden = true;
    input.removeAttribute('aria-invalid');
    btn.disabled = true;

    var pet = state.info && state.info.line ? petNameOf(state.info.line) : (state.preview ? 'Bailey' : '');
    var done = function () {
      fill(el, pet, email);
      setView(el, 'done');
      btn.disabled = false;
    };

    if (state.preview) { done(); return; }

    send(el.getAttribute('data-endpoint'), buildPayload(email));
    saveEmailOnCart(email);
    fbqLead();
    if (!isTest()) lsSet(K.submitted, '1');
    track('submit', { trigger: state.trigger });
    done();
  }

  /* --------------------------------------------- keyboard on phones */

  function watchKeyboard(el) {
    var vv = window.visualViewport;
    if (!vv) return;
    var update = function () {
      if (!el.open) return;
      var shrunk = vv.height < window.innerHeight * 0.8;
      el.classList.toggle('bbcp--kb', shrunk);
      el.style.setProperty('--bbcp-vh', vv.height + 'px');
      el.style.setProperty('--bbcp-top', vv.offsetTop + 'px');
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    el.addEventListener('focusin', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-bbcp-email]')) {
        setTimeout(function () {
          update();
          var btn = el.querySelector('.bbcp__form .bbcp__btn');
          if (btn && btn.scrollIntoView) btn.scrollIntoView({ block: 'nearest' });
        }, 350);
      }
    });
  }

  /* ------------------------------------------------------- Trigger A */

  var tickTimer = null;

  function delayMs(el) {
    if (isTest()) return 5000;
    var s = parseInt(el.getAttribute('data-delay'), 10);
    return (isNaN(s) ? 20 : s) * 1000;
  }

  function armA() { ssSet(K.armed, '1'); ssSet(K.elapsed, '0'); }
  function disarmA() { ssDel(K.armed); ssDel(K.elapsed); }

  function startTicker() {
    if (tickTimer) return;
    tickTimer = setInterval(function () {
      var el = dlg();
      if (!el || ssGet(K.armed) !== '1') return;
      if (document.visibilityState !== 'visible') return;   // only while our store is on screen
      if (state.open || otherDialogOpen()) return;             // never on top of the cart or a modal
      var elapsed = (parseInt(ssGet(K.elapsed) || '0', 10) || 0) + 1000;
      ssSet(K.elapsed, String(elapsed));
      if (elapsed >= delayMs(el)) {
        disarmA();
        show('a');
      }
    }, 1000);
  }

  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer-component dialog, dialog.cart-drawer__dialog');
    if (!drawer) return;
    var wasOpen = drawer.hasAttribute('open');
    new MutationObserver(function () {
      var isOpen = drawer.hasAttribute('open');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      if (isOpen) { disarmA(); return; }
      if (onDrawerClosed) setTimeout(onDrawerClosed, 400);
      // Closed. If Checkout was just tapped, the page is leaving: do not arm.
      if (ssGet(K.checkout) === '1') return;
      armA();
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  function watchCartPage() {
    var path = window.location.pathname.replace(/\/$/, '');
    if (!/\/cart$/.test(path)) return;
    window.addEventListener('pagehide', function () {
      if (ssGet(K.checkout) === '1') return;
      armA();
    });
  }

  /* ------------------------------------------------------- Trigger B */

  function watchCheckout() {
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest(CHECKOUT_SELECTOR) : null;
      if (!t || (t.closest && t.closest('dialog[data-bbcp]') && !t.hasAttribute('data-bbcp-checkout'))) return;
      ssSet(K.checkout, '1');
      disarmA();
    }, true);
    document.addEventListener('submit', function (e) {
      var f = e.target;
      var s = e.submitter;
      var action = (f && f.getAttribute && f.getAttribute('action')) || '';
      if ((s && s.name === 'checkout') || /\/checkout/.test(action)) { ssSet(K.checkout, '1'); disarmA(); }
    }, true);
  }

  function checkReturnFromCheckout() {
    if (ssGet(K.checkout) !== '1') return;
    ssDel(K.checkout);
    setTimeout(function () { show('b'); }, 3000);
  }

  /* ------------------------------------------------------- Trigger C */

  var guardPushed = false;
  var exitShown = false;
  var onDrawerClosed = null;

  function exitIntentOn(el) { return el.getAttribute('data-exit-intent') === 'true'; }

  function isTouch() {
    return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) ||
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  }

  function setupExitIntent(el) {
    if (!exitIntentOn(el)) return;

    if (!isTouch()) {
      // Desktop: the cursor leaves through the top of the window.
      document.addEventListener('mouseout', function (e) {
        if (exitShown || e.relatedTarget || e.toElement) return;
        if (typeof e.clientY === 'number' && e.clientY > 10) return;
        exitShown = true;
        show('c').then(function (ok) { if (!ok) exitShown = false; });
      });
      return;
    }

    // Phones: one history entry in front of the page. The first Back lands on
    // it and shows the pop-up instead of leaving; the next Back leaves as normal.
    // Only placed when this shopper could actually see the pop-up.
    var placeGuard = function () {
      if (guardPushed || exitShown || !shopperAllowed(el)) return;
      // Never under an open cart drawer: it owns the Back button while open.
      if (otherDialogOpen()) return;
      fetchCart().then(function (cart) {
        if (guardPushed || otherDialogOpen() || !readCart(cart).line) return;
        try {
          history.pushState({ bbcpGuard: true }, '');
          guardPushed = true;
        } catch (e) {}
      }).catch(function () {});
    };

    window.addEventListener('popstate', function (e) {
      if (!guardPushed || exitShown) return;
      var st = e.state || {};
      // The cart drawer pushes and pops its own state on phones; ignore those.
      if (st.bbcpGuard || st.cartDrawerOpen) return;
      exitShown = true;
      guardPushed = false;
      show('c').then(function (ok) {
        if (!ok) { try { history.back(); } catch (err) {} }
      });
    });

    // Landed back on a guard entry this page placed earlier (reload, or Back
    // from the next page): reuse it rather than stacking a second one. If the
    // pop-up is no longer allowed, the popstate handler above steps over it.
    try { if (history.state && history.state.bbcpGuard) guardPushed = true; } catch (e) {}

    placeGuard();
    onDrawerClosed = placeGuard;
    document.addEventListener('cart:update', function () { setTimeout(placeGuard, 600); });
  }

  /* ------------------------------------------------------- wiring */

  function bindDialog(el) {
    if (el.__bbcpBound) return;
    el.__bbcpBound = true;
    el.querySelector('[data-bbcp-form]').addEventListener('submit', onSubmit);
    el.querySelector('[data-bbcp-email]').addEventListener('input', function () {
      el.querySelector('[data-bbcp-error]').hidden = true;
      this.removeAttribute('aria-invalid');
    });
    el.querySelectorAll('[data-bbcp-close]').forEach(function (b) {
      b.addEventListener('click', function () { closeDialog('dismiss'); });
    });
    el.addEventListener('cancel', function (e) { e.preventDefault(); closeDialog('escape'); });
    el.addEventListener('click', function (e) { if (e.target === el) closeDialog('outside'); });
    var go = el.querySelector('[data-bbcp-checkout]');
    if (go) go.addEventListener('click', function () { ssSet(K.checkout, '1'); disarmA(); });
    watchKeyboard(el);
  }

  function previewInEditor(el) {
    state.preview = true;
    state.info = null;
    fill(el, 'Bailey', 'you@email.com');
    setView(el, 'offer');
    if (!el.open) openDialog(el);
  }

  function init() {
    var el = dlg();
    if (!el) return;
    bindDialog(el);

    if (el.getAttribute('data-preview') === 'true') { previewInEditor(el); return; }

    watchCheckout();
    watchDrawer();
    watchCartPage();
    startTicker();
    setupExitIntent(el);
    document.addEventListener('cart:update', function () { disarmA(); });
  }

  // Trigger B check runs on every page show, including a Back from checkout
  // restored from the browser cache (no new DOMContentLoaded in that case).
  window.addEventListener('pageshow', function () {
    if (!dlg() || (dlg().getAttribute('data-preview') === 'true')) return;
    state.shownThisPage = false;
    checkReturnFromCheckout();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-run when the section is re-rendered after a settings change.
  document.addEventListener('shopify:section:load', function () {
    state.el = null;
    var el = dlg();
    if (!el) return;
    bindDialog(el);
    if (el.getAttribute('data-preview') === 'true') previewInEditor(el);
  });
})();
